/**
 * Login Page - PRODUCTION + TEST LOGIN (preview only)
 * Firebase Auth (Google) + optional test login for preview
 */
import { useState, useEffect } from "react";
import { logger } from "../../utils/logger";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { signInWithGoogle } from "@/firebase/authProviders";
import { isFirebaseConfigured } from "@/firebase/config";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const isPreview = BACKEND_URL?.includes('preview') || window.location.hostname.includes('preview') || window.location.hostname === 'localhost';

const LoginPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshUser } = useAuth();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Standard Google login
  const handleGoogleLogin = async () => {
    if (googleLoading) return;
    
    if (!isFirebaseConfigured()) {
      toast.error('Firebase nie jest skonfigurowany');
      return;
    }

    setGoogleLoading(true);
    try {
      const result = await signInWithGoogle(false);
      if (result?.user) {
        toast.success(`Witaj, ${result.user.displayName || result.user.email}!`);
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      logger.error('[Login] Google sign-in error:', error);
      if (error.code !== 'auth/popup-closed-by-user') {
        toast.error(error.message || 'Błąd logowania');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  // Google login with account selector (force choose account) — USUNIĘTE 2026-05-31
  // Powód: button "Zaloguj innym kontem" prowadził donikąd na mobile/preview.
  // Standardowy `handleGoogleLogin` wystarcza; konto można zmienić wylogowując się
  // z Google w przeglądarce (lub przez "Wybierz inne konto" w popupie Google).

  // Test login handler (preview only)
  const handleTestLogin = async () => {
    if (testLoading) return;
    
    setTestLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/test-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: 'test@pax.local',
          name: 'Test User'
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Błąd logowania testowego');
      }
      
      const userData = await response.json();
      toast.success(`Witaj, ${userData.name}! (tryb testowy)`);
      
      if (refreshUser) {
        await refreshUser();
      }
      navigate('/dashboard', { replace: true });
      
    } catch (error) {
      logger.error('[Login] Test login error:', error);
      toast.error(error.message || 'Błąd logowania testowego');
    } finally {
      setTestLoading(false);
    }
  };

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Ładowanie...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container" data-testid="login-page">
      <div className="login-form-side">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[#0F1F3D] tracking-tight">
              PAX
            </h1>
            <p className="text-lg font-semibold text-gray-700 mt-2">
              Płatności Automatyczne dla Przedsiębiorców
            </p>
          </div>
          
          <div className="space-y-3">
            {/* Main Google Login — dostępne w obu środowiskach (PREVIEW + PROD) */}
            <Button 
              onClick={handleGoogleLogin}
              disabled={googleLoading || !isFirebaseConfigured()}
              className="w-full btn-primary flex items-center justify-center gap-3 h-12"
              data-testid="google-login-btn"
            >
              {googleLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              Zaloguj się przez Google
            </Button>

            {!isFirebaseConfigured() && (
              <p className="text-xs text-red-500 text-center">
                Firebase nie jest skonfigurowany. Skontaktuj się z administratorem.
              </p>
            )}

            {/* Test Login - tylko dla PREVIEW */}
            {isPreview && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center mb-3">
                  Środowisko PREVIEW — dostępne logowanie testowe
                </p>
                <Button 
                  onClick={handleTestLogin}
                  disabled={testLoading}
                  variant="outline"
                  className="w-full flex items-center justify-center gap-2 h-10 border-amber-500 text-amber-700 hover:bg-amber-50"
                  data-testid="test-login-btn"
                >
                  {testLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FlaskConical className="w-4 h-4" />
                  )}
                  Logowanie testowe
                </Button>
              </div>
            )}
          </div>
          
          <p className="text-xs text-gray-400 mt-8 text-center">
            Logując się akceptujesz regulamin i politykę prywatności
          </p>
        </div>
      </div>
      
      <div 
        className="login-image-side"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1693269276499-5d71d866785f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjBnZW9tZXRyaWMlMjBmaW5hbmNlJTIwYmx1ZSUyMHdoaXRlfGVufDB8fHx8MTc3MTE5MjM4MHww&ixlib=rb-4.1.0&q=85')`
        }}
      />
    </div>
  );
};

export default LoginPage;
